import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Navbar, Footer } from "../components";
import api from "../utils/base_url";
import toast from "react-hot-toast";
import "../styles/OrderTracking.css";

const OrderTracking = () => {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  // Feedback modal
  const [showModal, setShowModal] = useState(false);
  const [feedbackProduct, setFeedbackProduct] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");

  /* ── Helpers ── */
  const formatStatus = (value) => {
    if (!value) return "";
    return value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const statusKey = (status) => {
    const valid = ["pending", "order_confirmed", "shipped", "delivered", "cancelled", "returned"];
    return valid.includes(status) ? status : "pending";
  };

  // Combos are a bundle of products, not a single reviewable item — reviews
  // stay per-product, so the "★ Review" action only shows on product lines.
  const isComboItem = (item) => item.item_type === "combo";

  const includedItemsText = (item) =>
    (item.included_items || []).map((ci) => `${ci.product_name} x${ci.quantity}`).join(", ");

  /* ── Feedback ── */
  const openFeedbackModal = async (item) => {
    const productId = item?.product || item?.product_id || item?.id;
    if (!productId) return toast.error("Invalid product");
    setFeedbackProduct({ id: productId, name: item?.product_name || "Product" });
    setRating(0);
    setComment("");
    setShowModal(true);
    try {
      const res = await api.get(`feedback/${productId}/`);
      if (res.data?.data) {
        setRating(Number(res.data.data.rating || 0));
        setComment(res.data.data.comment || "");
      }
    } catch (err) {
      if (err.response?.status !== 404) console.warn("Feedback fetch:", err);
    }
  };

  const submitFeedback = async () => {
    if (!feedbackProduct?.id) return toast.error("Invalid product");
    if (!rating) return toast.error("Please select a star rating");
    if (comment.trim().length > 1000) return toast.error("Comment can't exceed 1000 characters");
    try {
      await api.post(`feedback/${feedbackProduct.id}/`, { rating, comment });
      toast.success("Feedback submitted!");
      setShowModal(false);
    } catch {
      toast.error("Failed to submit feedback");
    }
  };

  /* ── Invoice ── */
  const downloadInvoice = async () => {
    try {
      const res = await api.get(`orderspdf/${order.id}/`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${order.order_number}.pdf`);
      document.body.appendChild(link);
      link.click();
    } catch {
      toast.error("Failed to download invoice");
    }
  };

  /* ── Fetch order ── */
  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const res = await api.get(`order-tracking/${orderNumber}/`);
        setOrder(res.data.data);
      } catch {
        toast.error("Failed to fetch order details");
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderNumber]);

  /* ── Loading / Error ── */
  if (loading) return (
    <><Navbar />
      <div className="ot-state-center">
        <p className="ot-state-title">Loading order…</p>
      </div>
    <Footer /></>
  );

  if (!order) return (
    <><Navbar />
      <div className="ot-state-center">
        <p className="ot-state-title">No order found</p>
        <Link to="/" className="ot-btn-outline">Back to Home</Link>
      </div>
    <Footer /></>
  );

  return (
    <>
      <Navbar />
      <div className="ot-page">
        <div className="container">
          <div className="ot-stack">

            {/* ── Order Hero ── */}
            <div className="ot-card">
              <div className="ot-card-header">
                <span className="ot-card-title">Order Tracking</span>
              </div>
              <div className="ot-card-body">

                {/* Order number row */}
                <div className="ot-hero-row">
                  <span className="ot-hero-label">Order :</span>
                  <span className="ot-hero-id">#{order.order_number}</span>
                </div>

                {/* Meta grid */}
                <div className="ot-meta-grid">
                  <div className="ot-meta-item">
                    <span className="ot-meta-label">Status :</span>
                    <span className={`ot-status-badge ${statusKey(order.status)}`}>
                      {formatStatus(order.status)}
                    </span>
                  </div>

                  <div className="ot-meta-item">
                    <span className="ot-meta-label">Ordered at :</span>
                    <span className="ot-meta-value">
                      {new Date(order.ordered_at).toLocaleString()}
                    </span>
                  </div>

                  {order.preferred_courier_service && (
                    <div className="ot-meta-item">
                      <span className="ot-meta-label">Courier</span>
                      <span className="ot-meta-value">{order.preferred_courier_service}</span>
                    </div>
                  )}

                  {order.courier_number && (
                    <div className="ot-meta-item">
                      <span className="ot-meta-label">Tracking no.</span>
                      <span className="ot-meta-value">{order.courier_number}</span>
                    </div>
                  )}

                  {order.delivered_at && (
                    <div className="ot-meta-item">
                      <span className="ot-meta-label">Delivered at</span>
                      <span className="ot-meta-value">
                        {new Date(order.delivered_at).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ── Addresses + Payment ── */}
            <div className="ot-two-col">
              <div className="ot-card">
                <div className="ot-card-header">
                  <span className="ot-card-title">Addresses</span>
                </div>
                <div className="ot-card-body ot-detail-row">
                  <div className="ot-detail-item-address">
                    <span className="ot-detail-key">Billing address :</span>
                    <span className="ot-detail-val">{order.billing_address}</span>
                  </div>
                  <div className="ot-detail-item-address">
                    <span className="ot-detail-key">Shipping address :</span>
                    <span className="ot-detail-val">{order.shipping_address}</span>
                  </div>
                </div>
              </div>

              <div className="ot-card">
                <div className="ot-card-header">
                  <span className="ot-card-title">Payment :</span>
                </div>
                <div className="ot-card-body ot-detail-row">
                  <div className="ot-detail-item">
                    <span className="ot-detail-key">Metho :</span>
                    <span className="ot-detail-val">
                      {order.payment_method === "cod" ? "Cash on Delivery" : "Online Payment"}
                    </span>
                  </div>
                  <div className="ot-detail-item">
                    <span className="ot-detail-key">Payment status :</span>
                    <span className={`ot-payment-badge ${order.payment_status}`}>
                      {formatStatus(order.payment_status)}
                    </span>
                  </div>
                  {order.payment?.razorpay_order_id && (
                    <div className="ot-detail-item">
                      <span className="ot-detail-key">Payment ID :</span>
                      <span className="ot-detail-val">{order.payment.razorpay_order_id}</span>
                    </div>
                  )}
                  {order.payment?.razorpay_payment_id && (
                    <div className="ot-detail-item">
                      <span className="ot-detail-key">Order ID :</span>
                      <span className="ot-detail-val">{order.payment.razorpay_payment_id}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ── Items ── */}
            <div className="ot-card">
              <div className="ot-card-header">
                <span className="ot-card-title">Items</span>
              </div>
              <div style={{ padding: 0 }}>
                <div className="ot-table-wrap">
                  <table className="ot-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Variant</th>
                        <th>Qty</th>
                        <th>Price</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.items.map((item, idx) => (
                        <tr key={item.product ?? `combo-${item.combo}-${idx}`}>
                          <td>
                            <div className="ot-product-cell">
                              <span className="ot-product-name">
                                {item.product_name}
                                {isComboItem(item) && (
                                  <span
                                    style={{
                                      marginLeft: 6,
                                      fontSize: 10,
                                      fontWeight: 600,
                                      textTransform: "uppercase",
                                      letterSpacing: "0.5px",
                                      color: "#7a5b00",
                                      background: "#fdf1cf",
                                      border: "1px solid #e9c65f",
                                      borderRadius: 4,
                                      padding: "1px 6px",
                                    }}
                                  >
                                    Combo
                                  </span>
                                )}
                              </span>
                              {isComboItem(item) && item.included_items?.length > 0 && (
                                <div className="ot-td-muted" style={{ marginTop: 2 }}>
                                  Includes: {includedItemsText(item)}
                                </div>
                              )}
                              {order.status === "delivered" && !isComboItem(item) && (
                                <button className="ot-btn-review" onClick={() => openFeedbackModal(item)}>
                                  ★ Review
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="ot-td-muted">
                            {isComboItem(item)
                              ? "—"
                              : ["size", "color"].includes(item.quantity_unit?.toLowerCase())
                                ? item.quantity_value
                                : item.quantity_value && item.quantity_unit
                                  ? `${Number(item.quantity_value)} ${item.quantity_unit}`
                                  : "—"}
                          </td>
                          <td>{item.quantity}</td>
                          <td className="ot-td-amount">₹{item.price.toLocaleString()}</td>
                          <td className="ot-td-amount">₹{item.total.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* ── Order Summary ── */}
            <div className="ot-card">
              <div className="ot-card-header">
                <span className="ot-card-title">Order Summary</span>
              </div>
              <div className="ot-card-body">
                <div className="ot-summary-list">
                  <div className="ot-summary-row">
                    <span>Subtotal</span>
                    <span className="ot-summary-amount">
                      ₹{(Number(order.subtotal || 0) + Number(order.tax || 0)).toLocaleString()}
                    </span>
                  </div>
                  <div className="ot-summary-row">
                    <span>Shipping</span>
                    <span className="ot-summary-amount">₹{order.shipping_cost.toLocaleString()}</span>
                  </div>
                  <div className="ot-summary-row total">
                    <span>Total</span>
                    <span className="ot-summary-amount">₹{order.total_amount.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Actions ── */}
            <div className="ot-actions">
              <Link to="/orderhistory" className="ot-btn-outline">← Back to orders</Link>
              <button type="button" className="ot-btn-primary" onClick={downloadInvoice}>
                Download invoice
              </button>
            </div>

          </div>
        </div>
      </div>
      <Footer />

      {/* ── Feedback Modal ── */}
      {showModal && (
        <div
          className="ot-modal-overlay"
          onClick={(e) => e.target === e.currentTarget && setShowModal(false)}
        >
          <div className="ot-modal-box" role="dialog" aria-modal="true">
            <div className="ot-modal-header">
              <p className="ot-modal-title">Leave a review</p>
              <p className="ot-modal-subtitle">{feedbackProduct?.name}</p>
            </div>
            <div className="ot-modal-body">
              <div className="ot-stars" role="radiogroup" aria-label="Star rating">
                {[1, 2, 3, 4, 5].map((star) => (
                  <span
                    key={star}
                    className={`ot-star ${star <= (hover || rating) ? "active" : ""}`}
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHover(star)}
                    onMouseLeave={() => setHover(0)}
                    role="radio"
                    aria-checked={star === rating}
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && setRating(star)}
                  >★</span>
                ))}
              </div>
              <div>
                <textarea
                  className="ot-textarea"
                  rows={4}
                  placeholder="Share your experience…"
                  value={comment}
                  onChange={(e) => setComment(e.target.value.slice(0, 1000))}
                />
                <p className="ot-char-count">{comment.length} / 1000</p>
              </div>
            </div>
            <div className="ot-modal-footer">
              <button className="ot-btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="ot-btn-primary" onClick={submitFeedback}>Submit</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrderTracking;